import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy,
  OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AttributionDao } from '@app/dao/attribution/attribution.dao';
import { PledgeRead } from '@app/forms/subscription/definition/interface/Pledge';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { combineLatest, Observable, throwError, catchError, delay, finalize, map, shareReplay, take, tap } from 'rxjs';
import { InfoOption } from '../../components/info/info.component';
import { CommandEnum } from '../../definition/interface/enum';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';

export interface Document {
  gedId: string;
  prGedId: number;
  index: number;
  name: string;
  signatures: any[];
  universignCheckBoxes?: string[];
  current?: boolean;
}

/** Une ligne dans l'accordion V2 (un PrPackDocument). */
interface V2DocRow {
  packDocumentId: number;
  documentType: string;
  label: string;
  gedDocumentId: number | null;
  expanded: boolean;
  loadingPdf: boolean;
  pdfSafeUrl: SafeResourceUrl | null;
  pdfObjectUrl: string | null;
  pdfRequest: Observable<string> | null;
  pdfError: boolean;
  downloading: boolean;
}

/** Une section dans l'accordion V2 (un PrPack). */
interface V2PackSection {
  packType: string;
  title: string;
  rows: V2DocRow[];
}

@Component({
    selector: 'document-compliance',
    templateUrl: './document-compliance.component.html',
    styleUrls: ['./document-compliance.component.scss'],
    standalone: false
})
export class DocumentComplianceComponent implements OnInit, OnDestroy, AfterViewInit {

  /** object URLs des previews PDF, révoquées au destroy (fuite mémoire). */
  private createdObjectUrls: string[] = [];

  ngOnDestroy(): void {
    this.createdObjectUrls.forEach(url => window.URL.revokeObjectURL(url));
    this.createdObjectUrls = [];
  }

  private trackObjectUrl(url: string): string {
    this.createdObjectUrls.push(url);
    return url;
  }
  optionInfo1: InfoOption = {
    icon: 'warning',
    backgroundColor: '#FFF3E6',
    iconColor: '#f9a11d',
    textColor: '#f9a11d',
  };
  optionInfo2: InfoOption = {
    icon: 'check',
    backgroundColor: '#E6F9F0',
    iconColor: '#00b67a',
    textColor: '#00b67a',
  };

  operation: string = 'SUBSCRIPTION';
  pledge: PledgeRead;
  safeUrl;
  tabs: Document[] = [];
  loading = false;
  loadingMessage = '';
  isCristalisOrganization: boolean = false;
  cristalisOrganizationId: number = 251251251;

  progress = 0;

  internalError: string = null;

  /** sections accordion V2 (Pack Régl + Adéq + BS) */
  v2Sections: V2PackSection[] = [];

  constructor(
    private subscriptionFacade: SubscriptionFacadeService,
    private httpApiService: HttpApiService,
    private sanitizer: DomSanitizer,
    private attributionDao: AttributionDao,
    private cdr: ChangeDetectorRef,
  ) {}

  get isV2(): boolean {
    return this.pledge?.legacyFlow === false;
  }

  ngAfterViewInit(): void {}

  ngOnInit(): void {
    this.pledge = this.subscriptionFacade.getPledge();
    this.operation = this.pledge.operation || 'SUBSCRIPTION';
    combineLatest([this.attributionDao.getOrganization()]).subscribe(
      ([organization]) => {
        this.isCristalisOrganization =
          this.cristalisOrganizationId === organization.id;
      },
    );

    // Séquençage V2 — En V2 (legacyFlow=false), les PDFs preview ont déjà été
    // générés par PREVIEW_PACK_PDFS au Continuer du pack-builder. On lit
    // directement pledge.packs[].documents[].gedDocumentId pour construire les tabs.
    if (this.pledge?.legacyFlow === false) {
      this.updateDocumentListV2();
      return;
    }

    const { progress } = this.pledge;
    if (['OPEN', 'DRAFT'].includes(progress)) {
      this.regenerateDocuments();
    } else {
      this.updateDocumentList();
    }
  }

  updateDocumentList(): void {
    const filteredDocuments =
      this.pledge?.documents?.sort((a, b) =>
        a.name === 'Pack juridique' ? 1 : -1,
      ) || [];
    this.tabs = [...filteredDocuments];
    // garde : liste vide après filtre → tabs[0] était undefined (exception).
    if (this.tabs.length) {
      this.tabs[0].current = true;
      const firstTab = this.tabs[0];
      this.downloadDocument(firstTab.prGedId, firstTab.name).subscribe();
    }
  }

  /**
   * Séquençage V2 — Construit l'accordion harmonisé avec PackBuilderComponent
   * à partir de pledge.packs[].documents[]. Sections par pack (Réglementaire / Adéquation / BS),
   * docs dépliables avec PDF preview inline (lazy-load au clic, cf. onV2DocClick).
   *
   * <p>Docs sans {@code gedDocumentId} (typiquement Pack BS dont le PDF est généré à l'envoi
   * Universign) sont listés avec un badge « PDF disponible après envoi ».</p>
   */
  private updateDocumentListV2(): void {
    const PACK_TITLES: Record<string, string> = {
      REGLEMENTAIRE: 'Pack Réglementaire',
      ADEQUATION: 'Pack Adéquation',
      BS: 'Pack BS',
    };
    const packTypeOrder = ['REGLEMENTAIRE', 'ADEQUATION', 'BS'];
    const packs = this.pledge?.packs ?? [];

    this.v2Sections = packTypeOrder
      .map(packType => {
        const pack = packs.find(p => p.type === packType);
        if (!pack || (pack.documents ?? []).length === 0) {
          return null;
        }
        return {
          packType,
          title: PACK_TITLES[packType] ?? packType,
          rows: pack.documents.map(doc => ({
            packDocumentId: doc.id!,
            documentType: doc.documentType,
            label: this.documentLabel(doc.documentType, doc.title),
            gedDocumentId: doc.gedDocumentId ?? null,
            expanded: false,
            loadingPdf: false,
            pdfSafeUrl: null,
            pdfObjectUrl: null,
            pdfRequest: null,
            pdfError: false,
            downloading: false,
          })) as V2DocRow[],
        } as V2PackSection;
      })
      .filter((s): s is V2PackSection => s !== null);
  }

  /**
   * Toggle de l'accordion + lazy load du PDF preview au premier dépliage.
   * Pack BS sans gedDocumentId : pas de download (badge "PDF disponible après envoi").
   */
  onV2DocClick(row: V2DocRow): void {
    row.expanded = !row.expanded;
    if (row.expanded && !row.pdfSafeUrl && !row.loadingPdf && row.gedDocumentId) {
      this.loadV2DocPdf(row);
    }
  }

  private loadV2DocPdf(row: V2DocRow): void {
    if (!row.gedDocumentId || row.loadingPdf) {
      return;
    }
    row.loadingPdf = true;
    row.pdfError = false;
    this.cdr.markForCheck();

    this.fetchPdfObjectUrl(row)
      .pipe(
        finalize(() => {
          row.loadingPdf = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe();
  }

  onV2DownloadClick(row: V2DocRow): void {
    if (!row.gedDocumentId || row.downloading) {
      return;
    }
    row.pdfError = false;
    if (row.pdfObjectUrl) {
      this.saveObjectUrl(row.pdfObjectUrl, row.label);
      return;
    }
    row.downloading = true;
    row.pdfError = false;
    this.cdr.markForCheck();

    this.fetchPdfObjectUrl(row)
      .pipe(
        finalize(() => {
          row.downloading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe((objectUrl: string) => this.saveObjectUrl(objectUrl, row.label));
  }

  private fetchPdfObjectUrl(row: V2DocRow): Observable<string> {
    if (row.pdfRequest) {
      return row.pdfRequest;
    }
    const documentUrl = `/accounts/${this.pledge.accountId}/documents/${row.gedDocumentId}`;
    const options = { responseType: 'arraybuffer' };
    row.pdfRequest = this.httpApiService.getWithOptions(documentUrl, options).pipe(
      take(1),
      map((content: ArrayBuffer) => {
        const blob = new Blob([content], { type: 'application/pdf' });
        const dataUrl = this.trackObjectUrl(window.URL.createObjectURL(blob));
        row.pdfObjectUrl = dataUrl;
        row.pdfSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(dataUrl);
        return dataUrl;
      }),
      catchError((err: HttpErrorResponse) => {
        row.pdfError = true;
        row.pdfRequest = null;
        this.cdr.markForCheck();
        console.error(err);
        return throwError(err);
      }),
      shareReplay(1),
    );
    return row.pdfRequest;
  }

  private saveObjectUrl(objectUrl: string, label: string): void {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `${label}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private documentLabel(documentType: string, title?: string | null): string {
    if (title) {
      return title;
    }
    switch (documentType) {
      case 'DOCUMENT_ENTREE_EN_RELATION':
        return "Document d'Entrée en Relation";
      case 'LETTRE_DE_MISSION':
        return 'Lettre de mission';
      case 'FICHE_CONNAISSANCE_CLIENT':
        return 'Fiche de connaissance client';
      case 'MANDAT_IAS':
        return 'Mandat IAS';
      case 'RCDA':
        return 'RCDA';
      case 'CONVENTION_RTO':
        return 'Convention RTO';
      case 'ANNEXE_LETTRE_DE_MISSION':
        return 'Annexe lettre de mission';
      case 'ANNEXE_PRESENTATION_SUPPORTS_ALLOCATION':
        return "Annexe présentation supports d'allocation";
      case 'BULLETIN_DE_SOUSCRIPTION':
        return 'Bulletin de souscription';
      case 'AVENANT':
        return 'Avenant';
      case 'PACK_JURIDIC_CIF':
        return 'Pack réglementaire - CIF';
      case 'PACK_JURIDIC_IAS':
        return 'Pack réglementaire - IAS';
      default:
        return documentType;
    }
  }

  regenerateDocuments() {
    this.internalError = null;
    this.subscriptionFacade.setShowSidebarStepper(false);
    this.loading = true;
    this.progress = 0;
    this.loadingMessage = 'Génération des documents…';

    this.subscriptionFacade
      .putPledge({
        ...this.pledge,
        commands: [CommandEnum.CONVERT_DOCUMENTS_TO_PDF],
      })
      .pipe(
        catchError((err: any) => {
          this.internalError = 'Erreur lors de la génération des documents.';
          return throwError(err);
        }),

        finalize(() => this.resetLoader()),
      )
      .subscribe((result: PledgeRead) => {
        this.pledge = result;
        this.updateDocumentList();
      });
  }

  private resetLoader() {
    this.loading = false;
    this.loadingMessage = '';
    this.progress = 0;
    this.subscriptionFacade.loaded();
    this.subscriptionFacade.setShowSidebarStepper(true);
  }

  downloadDocument(documentId: number, documentName: string): Observable<any> {
    this.loading = true;

    const documentUrl = `/accounts/${this.pledge.accountId}/documents/${documentId}`;
    const options = { responseType: 'arraybuffer' };

    return this.httpApiService.getWithOptions(documentUrl, options).pipe(
      take(1),
      delay(1000),
      tap((documentContent: ArrayBuffer) => {
        this.createIFrame(documentContent);
      }),
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        return throwError(error);
      }),
      finalize(() => {}),
    );
  }

  createIFrame(resp) {
    let blob = new Blob([resp], { type: 'application/pdf' });
    let dataUrl = this.trackObjectUrl(window.URL.createObjectURL(blob));

    this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(dataUrl);

    this.loading = false;
  }

  onTabClicked(tab: Document) {
    tab?.prGedId && this.downloadDocument(tab.prGedId, tab.name).subscribe();
  }
  prev() {
    this.subscriptionFacade.previousStep();
  }
  next() {
    this.subscriptionFacade.nextStep();
  }
}
